<template>
  <section v-if="sharedUsages && sharedUsages.length" class="mt-12">
    <div class="manual-areas-list">
      <div v-for="(usage, index) in sharedUsages" class="manual-areas-row" :key="`usageCorrection__${index}`">
        <div class="manual-areas-cell manual-areas-title">
          {{ getUsageTitle(usage, index) }}
        </div>
        <div class="manual-areas-cell manual-areas-percent whitespace-nowrap">{{ Math.round(usage.ratio) }} %</div>
        <div class="manual-areas-cell manual-areas-area whitespace-nowrap">
          {{ formatNumber(getAreaForUsage(index)) }} {{ $t('general.sq_m') }}
        </div>
        <div class="manual-areas-cell manual-areas-current flex items-center gap-1 whitespace-nowrap">
          <span>
            {{ minutesPerSquareMeterForUsageAtIndexIgnoringManualArea(index) }}
            {{ $t('general.min_per_sq_m') }}
          </span>
          <button
            v-if="getTierBoundary(index)"
            v-tooltip="getTierBoundaryTooltip(index)"
            type="button"
            class="tier-boundary-trigger"
            :class="{ 'is-decided': getTierDecision(index) }"
            :aria-label="tierBoundaryTexts(index).modalTitle"
            data-testid="tier-boundary-trigger"
            @click.prevent="tierModalIndex = index">
            <IconAlertTriangle class="h-4 w-4" stroke="1.8" />
          </button>
        </div>

        <div class="manual-areas-input-cell">
          <div class="relative">
            <NumberInput
              :modelValue="getCorrectionForIndex(index).manualArea"
              placeholder="-"
              :formatBigNumber="true"
              :class="{
                'core-form-error': validator.getErrors(`usageCorrections.${index}.manualArea`),
              }"
              class="manual-area-input"
              @update:modelValue="onManualAreaChange(index, $event)" />
            <ErrorMessage :name="`usageCorrections.${index}.manualArea`" class="error-message" />
            <div class="manual-area-reset">
              <button
                v-if="showCloseButton(getCorrectionForIndex(index).manualArea)"
                class="flex h-6 w-6 items-center justify-center text-neutral-700 hover:text-black"
                @click.prevent="onResetManualArea(index)"
                :title="$t('project.general.sections.usage_definition.usage.tip.reset')">
                <IconX class="h-4 w-4" stroke="1.8" />
              </button>
            </div>
          </div>
          <div class="manual-areas-cell whitespace-nowrap">
            {{ $t('general.min_per_sq_m') }}
          </div>
        </div>
      </div>
    </div>
    <TierBoundaryModal
      :open="tierModalIndex !== null"
      :boundary="tierModalIndex === null ? null : getTierBoundary(tierModalIndex)"
      :decision="tierModalIndex === null ? null : getTierDecision(tierModalIndex)"
      :texts="tierBoundaryTexts(tierModalIndex ?? 0)"
      @confirm="onTierDecision"
      @close="tierModalIndex = null" />
  </section>
</template>

<script lang="ts" setup>
import NumberInput from '@/components/partials/inputs/NumberInput.vue'
import { IconAlertTriangle, IconX } from '@tabler/icons-vue'
import { useI18n } from 'vue-i18n'
import TierBoundaryModal, { type TierDecision } from '@/components/partials/modals/TierBoundaryModal.vue'
import type { SharedUsageItem, UsageCorrection } from '@/schemas/general/types'
import ErrorMessage from '@/components/shared/ErrorMessage.vue'
import { deepClone } from '@/helpers/objects_arrays'
import { formatNumberWithThousandSeparators } from '@/helpers/format'
import { getUsageDisplayTitle } from '@/helpers/usage-display'
import { useValidator } from '@/validation/form-validator'
import { useWidgetText } from '@/helpers/useWidgetText'
import { useContentStore } from '@/stores/content'

const { wt } = useWidgetText('widget-texts-project-general')
import { computed, ref, toRef, watch, type PropType } from 'vue'
import { useCalculatorForRole, type Role } from '@/stores/projectCenter/useCalculatorForRole'

const modelValue = defineModel({ type: Array as PropType<UsageCorrection[]> })
const props = defineProps<{
  sharedUsages: SharedUsageItem[]
  quantityArea: number
  role?: Role
}>()

const validator = useValidator()
const contentStore = useContentStore()
const setupCalculator = useCalculatorForRole(toRef(props, 'role'))

const minutesPerSquareMeterForUsageAtIndexIgnoringManualArea = computed(() => (index: number): number => {
  if (!setupCalculator.value) return 0
  return Math.round(
    /* @ts-ignore */
    setupCalculator.value.getMinutesPerSquareMeterForUsageAtIndexIgnoringManualArea(index)
  )
})

/*
 * VAL-102: area interval boundaries. When a usage area is close to an interval boundary, the
 * socket reports the neighbouring interval's value. Choosing it writes that value into the
 * existing manual min/m² field, so no calculation has to know about the decision. The decision
 * only counts for the area it was made for. VAL-162 switched this off through
 * TIER_BOUNDARY_CHOICE_ENABLED: the widget no longer asks for the boundary info, so no row shows
 * the hint.
 */
const { t } = useI18n()
const { wt: wtSetup } = useWidgetText('widget-texts-project-setup', toRef(props, 'role'))
const tierModalIndex = ref<number | null>(null)

function getTierBoundary(index: number) {
  return setupCalculator.value?.tierBoundariesForUsages?.[index] ?? null
}

function getTierDecision(index: number): TierDecision | null {
  const decision = getCorrectionForIndex(index).tierDecision
  return decision && decision.area === getAreaForUsage(index) && getTierBoundary(index) ? decision : null
}

/** Payload texts win; empty ones fall back to the widget defaults. Both may use the placeholders. */
function tierBoundaryText(key: string, index: number): string {
  const boundary = getTierBoundary(index)
  const params: Record<string, string> = {
    area: formatNumber(getAreaForUsage(index)),
    boundary: formatNumber(boundary?.boundary ?? 0),
    percent: String(boundary?.percent ?? ''),
    current: String(Math.round(boundary?.currentMinutes ?? 0)),
    adjacent: String(Math.round(boundary?.adjacentMinutes ?? 0)),
  }
  // Payload texts are plain strings; the widget defaults are interpolated by vue-i18n, which
  // would otherwise consume the placeholders and leave them empty.
  const payloadText = wtSetup('definition', 'tierBoundary', key)
  return payloadText
    ? payloadText.replace(/\{(\w+)\}/g, (match, name: string) => params[name] ?? match)
    : t(`project.setup.sections.definition.tier_boundary.${key}`, params)
}

function tierBoundaryTexts(index: number) {
  return {
    modalTitle: tierBoundaryText('modalTitle', index),
    modalText: tierBoundaryText('modalText', index),
    optionCurrent: tierBoundaryText('optionCurrent', index),
    optionAdjacent: tierBoundaryText('optionAdjacent', index),
  }
}

function getTierBoundaryTooltip(index: number) {
  return {
    text: tierBoundaryText(getTierDecision(index) ? 'tooltipDecided' : 'tooltipOpen', index),
    portal: true,
    theme: { 'max-width': '360px' },
  }
}

function onTierDecision(choice: TierDecision['choice']): void {
  const index = tierModalIndex.value
  const boundary = index === null ? null : getTierBoundary(index)
  tierModalIndex.value = null
  if (index === null || !boundary || !modelValue.value) return

  const corrections = deepClone(modelValue.value)
  const correction = corrections.find((c) => c.usageIndex === index)
  if (!correction) return
  correction.tierDecision = { choice, area: getAreaForUsage(index) }
  correction.manualArea = choice === 'adjacent' ? Math.round(boundary.adjacentMinutes) : undefined
  modelValue.value = corrections
}

// A decision belongs to one area: drop it, and the value it set, once the area changes or the
// area is no longer close to a boundary. The second check waits for the socket result.
watch(
  () => [props.sharedUsages.map((_, index) => getAreaForUsage(index)), setupCalculator.value?.tierBoundariesForUsages],
  () => {
    if (!modelValue.value?.some((c) => c.tierDecision)) return
    const boundariesLoaded = setupCalculator.value?.tierBoundariesForUsages?.length === props.sharedUsages.length

    const corrections = deepClone(modelValue.value)
    let changed = false
    for (const correction of corrections) {
      const decision = correction.tierDecision
      if (!decision) continue
      const outdated =
        decision.area !== getAreaForUsage(correction.usageIndex) ||
        (boundariesLoaded && !getTierBoundary(correction.usageIndex))
      if (!outdated) continue
      if (decision.choice === 'adjacent') correction.manualArea = undefined
      correction.tierDecision = null
      changed = true
    }
    if (changed) modelValue.value = corrections
  },
  { deep: true }
)

function getAreaForUsage(index: number): number {
  const usage = props.sharedUsages[index]
  if (!usage) return 0
  return Math.round((props.quantityArea * usage.ratio) / 100)
}

function formatNumber(value: number): string {
  return formatNumberWithThousandSeparators(value)
}

function getUsageTitle(usage: SharedUsageItem, index: number): string {
  return getUsageDisplayTitle(
    usage as SharedUsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null },
    wt,
    {
      allUsages: props.sharedUsages as Array<
        SharedUsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null }
      >,
      index,
      resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
    }
  )
}

// Ensure corrections array has an entry for each shared usage
watch(
  () => props.sharedUsages,
  (usages) => {
    if (!modelValue.value || !usages) return

    const corrections = deepClone(modelValue.value)
    let changed = false

    usages.forEach((_, index) => {
      const existing = corrections.find((c) => c.usageIndex === index)
      if (!existing) {
        corrections.push({
          usageIndex: index,
          manualArea: undefined,
          manualComplexity: undefined,
          x1: null,
          y1: null,
          x2: null,
          y2: null,
          x3: null,
          y3: null,
          x4: null,
          y4: null,
          problem1: undefined,
          problem2: undefined,
          problem3: undefined,
          problem4: undefined,
        })
        changed = true
      }
    })

    // Remove corrections for usages that no longer exist
    const filteredCorrections = corrections.filter((c) => c.usageIndex < usages.length)
    if (filteredCorrections.length !== corrections.length) {
      changed = true
    }

    if (changed) {
      modelValue.value = filteredCorrections
    }
  },
  { immediate: true }
)

function getCorrectionForIndex(index: number): UsageCorrection {
  const existing = modelValue.value?.find((c) => c.usageIndex === index)
  if (existing) return existing

  // Return a default (will be added by the watch)
  return {
    usageIndex: index,
    manualArea: undefined,
    manualComplexity: undefined,
    x1: null,
    y1: null,
    x2: null,
    y2: null,
    x3: null,
    y3: null,
    x4: null,
    y4: null,
    problem1: undefined,
    problem2: undefined,
    problem3: undefined,
    problem4: undefined,
  }
}

function onManualAreaChange(index: number, value: number | undefined): void {
  if (!modelValue.value) return

  const corrections = deepClone(modelValue.value)
  const correctionIndex = corrections.findIndex((c) => c.usageIndex === index)

  if (correctionIndex >= 0) {
    corrections[correctionIndex].manualArea = value
    // VAL-102: a value typed by hand replaces an interval decision. Set to null instead of
    // deleting the key, because the store merges patches deeply and would keep the old value.
    if (corrections[correctionIndex].tierDecision) corrections[correctionIndex].tierDecision = null
  } else {
    corrections.push({
      usageIndex: index,
      manualArea: value,
      manualComplexity: undefined,
      x1: null,
      y1: null,
      x2: null,
      y2: null,
      x3: null,
      y3: null,
      x4: null,
      y4: null,
      problem1: undefined,
      problem2: undefined,
      problem3: undefined,
      problem4: undefined,
    })
  }

  modelValue.value = corrections
}

function showCloseButton(value: number | null | undefined) {
  return value != null && value >= 0
}

function onResetManualArea(index: number): void {
  if (!modelValue.value) return

  const corrections = deepClone(modelValue.value)
  const correctionIndex = corrections.findIndex((c) => c.usageIndex === index)

  if (correctionIndex >= 0) {
    corrections[correctionIndex].manualArea = undefined
    if (corrections[correctionIndex].tierDecision) corrections[correctionIndex].tierDecision = null
    modelValue.value = corrections
  }
}
</script>

<style lang="postcss" scoped>
.manual-areas-list {
  @apply overflow-hidden border-y border-neutral-300 bg-white;
}

.manual-areas-row {
  @apply grid gap-2 border-b border-neutral-300 px-3 py-3 text-sm text-neutral-700 last:border-b-0 md:items-center;
  grid-template-columns: minmax(0, 1fr);
}

.manual-areas-cell {
  @apply min-w-0;
}

.manual-areas-title {
  @apply overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium;
}

.manual-areas-input-cell {
  @apply flex min-w-0 items-center gap-2;
}

.manual-area-input {
  @apply !mb-0 !w-20 !bg-neutral-25 md:!w-24;
}

.tier-boundary-trigger {
  @apply inline-flex h-5 w-5 shrink-0 items-center justify-center text-primary-default;

  &.is-decided {
    @apply text-neutral-450;
  }
}

.manual-area-reset {
  @apply absolute right-1 top-2 flex w-6 items-center;
}

@media (min-width: 768px) {
  .manual-areas-row {
    grid-template-columns: minmax(0, 1fr) minmax(7rem, max-content) minmax(9rem, max-content);
  }
}

.manual-areas-percent,
.manual-areas-area {
  @apply hidden;
}

@media (min-width: 1024px) {
  .manual-areas-percent,
  .manual-areas-area {
    @apply block;
  }

  .manual-areas-row {
    grid-template-columns:
      minmax(0, 1fr)
      minmax(4rem, max-content)
      minmax(6.5rem, max-content)
      minmax(7rem, max-content)
      minmax(9rem, max-content);
  }
}
</style>
